import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../config";
import { Bell, Check, MessageCircle, TrendingUp, Award, Coins } from "lucide-react";

const ICON_MAP = {
  review_impact: TrendingUp,
  badge_earned: Award,
  coins_earned: Coins,
  routine_suggestion: MessageCircle,
  routine_worked: Check,
  default: MessageCircle,
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const res = await axios.get(`${API}/users/notifications`, { withCredentials: true });
        setNotifications(res.data.notifications || []);
        setUnread(res.data.unread_count || 0);
      } catch {}
    };
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/users/notifications/mark-read`, {}, { withCredentials: true });
      setUnread(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { setOpen(!open); if (!open && unread > 0) markAllRead(); }}
        className="relative p-2 rounded-full hover:bg-slate-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-muted-foreground" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-rose-500 rounded-full" data-testid="notification-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-lg border border-slate-100 z-50 overflow-hidden" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <span className="text-sm font-semibold">Notifications</span>
            {notifications.length > 0 && (
              <button onClick={markAllRead} className="text-xs text-[#5B6BA8] hover:underline flex items-center gap-1" data-testid="mark-all-read">
                <Check className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No notifications yet</p>
                <p className="text-xs text-muted-foreground mt-0.5">You'll be notified when your reviews help others</p>
              </div>
            ) : (
              notifications.slice(0, 15).map((n) => {
                const Icon = ICON_MAP[n.type] || ICON_MAP.default;
                return (
                  <div
                    key={n.notification_id}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-slate-50 last:border-0 transition-colors ${!n.read ? "bg-[#5B6BA8]/[0.03]" : ""}`}
                    data-testid={`notification-${n.notification_id}`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${!n.read ? "bg-[#5B6BA8]/10" : "bg-slate-100"}`}>
                      <Icon className={`w-4 h-4 ${!n.read ? "text-[#5B6BA8]" : "text-slate-400"}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-snug ${!n.read ? "font-medium" : "text-muted-foreground"}`}>{n.message}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-[#5B6BA8] mt-2 shrink-0" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
